import React from 'react';
import {
  Box,
  Chip,
  IconButton,
  MenuItem,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import dayjs from 'dayjs';
import { http, unwrapApiResponse } from '../api/http';
import { PageHeader } from '../components/PageHeader';
import { ErrorBanner } from '../components/ErrorBanner';
import {
  BarList,
  Legend,
  Panel,
  SegmentBar,
  StackedBarChart,
  StatTile,
  fmtMs,
  fmtNumber,
  fmtPercent,
  useChartColors,
} from '../components/charts';
import { formatDateTime, formatRelative } from '../utils/date';
import { useAppUi } from '../app/useAppUi';

const RANGES = ['24h', '7d', '30d', '90d'];

const rateTone = (part, total) => {
  if (!total) return undefined;
  const r = part / total;
  if (r >= 0.95) return 'good';
  if (r >= 0.8) return 'warning';
  return 'critical';
};

function StatusChip({ code }) {
  const color = code >= 500 ? 'error' : code === 429 || code === 401 ? 'warning' : 'default';
  return <Chip size="small" variant="outlined" color={color} label={code ?? '—'} sx={{ fontVariantNumeric: 'tabular-nums' }} />;
}

function EmptyRow({ colSpan, text }) {
  return (
    <TableRow>
      <TableCell colSpan={colSpan} sx={{ textAlign: 'center', color: 'text.disabled', py: 4, borderBottom: 'none' }}>
        {text}
      </TableCell>
    </TableRow>
  );
}

function SkeletonRows({ cols, rows = 4 }) {
  return Array.from({ length: rows }, (_, r) => (
    <TableRow key={r}>
      {Array.from({ length: cols }, (__, c) => (
        <TableCell key={c}>
          <Skeleton width={c === 0 ? '70%' : '50%'} />
        </TableCell>
      ))}
    </TableRow>
  ));
}

const num = { textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };

export function PartnerAnalyticsPage() {
  const { t } = useAppUi();
  const colors = useChartColors();
  const [range, setRange] = React.useState('7d');
  const [partnerId, setPartnerId] = React.useState('');
  const [partnerOptions, setPartnerOptions] = React.useState([]);
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [updatedAt, setUpdatedAt] = React.useState(null);

  React.useEffect(() => {
    http
      .get('/admin/partners', { params: { take: 200, page: 1 } })
      .then((res) => setPartnerOptions(unwrapApiResponse(res)?.partners || []))
      .catch(() => setPartnerOptions([]));
  }, []);

  const fetchData = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await http.get('/admin/partners/analytics', {
        params: { range, partner_id: partnerId || undefined },
      });
      setData(unwrapApiResponse(res));
      setUpdatedAt(new Date());
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [range, partnerId]);

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  const s = data?.summary || {};
  const total = Number(s.total) || 0;
  const failed = total - (Number(s.success) || 0);
  const tracked = Number(s.searches_tracked) || 0;

  const chartData = React.useMemo(
    () =>
      (data?.timeseries || []).map((b) => {
        const d = dayjs(b.bucket);
        return {
          label: data?.unit === 'hour' ? d.format('HH:00') : d.format('D MMM'),
          tooltipLabel: data?.unit === 'hour' ? d.format('D MMM, HH:00') : d.format('ddd, D MMM YYYY'),
          success: Number(b.success) || 0,
          failed: Number(b.failed) || 0,
        };
      }),
    [data]
  );

  const series = [
    { key: 'success', label: t('partnerAnalytics.success'), color: colors.series[0] },
    { key: 'failed', label: t('partnerAnalytics.failed'), color: colors.critical },
  ];

  const mix = [
    { key: 'ok', label: t('partnerAnalytics.mixSuccess'), value: Number(s.success) || 0, color: colors.good },
    { key: 'client', label: t('partnerAnalytics.mixClient'), value: Number(s.client_errors) || 0, color: colors.warning },
    { key: 'auth', label: t('partnerAnalytics.mixAuth'), value: Number(s.auth_failed) || 0, color: '#ec835a' },
    { key: 'rate', label: t('partnerAnalytics.mixRate'), value: Number(s.rate_limited) || 0, color: '#898781' },
    { key: 'server', label: t('partnerAnalytics.mixServer'), value: Number(s.server_errors) || 0, color: colors.critical },
  ];

  const countries = (data?.top_countries || []).map((c) => ({
    key: c.country_id,
    label: c.name,
    value: c.searches,
    sub: c.avg_results != null ? t('partnerAnalytics.avgTours', { n: fmtNumber(c.avg_results) }) : '',
  }));

  return (
    <Box>
      <PageHeader
        title={t('partnerAnalytics.title')}
        subtitle={
          updatedAt
            ? t('partnerAnalytics.subtitleUpdated', { time: formatDateTime(updatedAt) })
            : t('partnerAnalytics.subtitle')
        }
        right={
          <>
            <TextField
              select
              value={partnerId}
              onChange={(e) => setPartnerId(e.target.value)}
              sx={{ minWidth: 200 }}
              slotProps={{ select: { displayEmpty: true } }}
            >
              <MenuItem value="">{t('partnerAnalytics.allPartners')}</MenuItem>
              {partnerOptions.map((p) => (
                <MenuItem key={p.id} value={String(p.id)}>
                  {p.name || `#${p.id}`}
                </MenuItem>
              ))}
            </TextField>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={range}
              onChange={(_, v) => v && setRange(v)}
              sx={{ bgcolor: 'background.paper' }}
            >
              {RANGES.map((r) => (
                <ToggleButton key={r} value={r}>
                  {t(`partnerAnalytics.range_${r}`)}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <Tooltip title={t('common.refresh')}>
              <span>
                <IconButton
                  onClick={fetchData}
                  disabled={loading}
                  aria-label={t('common.refresh')}
                  sx={{ border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}
                  size="small"
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </>
        }
      />

      <ErrorBanner error={error} />

      <Box
        sx={{
          display: 'grid',
          gap: 1.5,
          mb: 1.5,
          gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))', lg: 'repeat(6, minmax(0, 1fr))' },
        }}
      >
        <StatTile
          loading={loading}
          label={t('partnerAnalytics.kpiRequests')}
          value={fmtNumber(total)}
          hint={t('partnerAnalytics.kpiRequestsHint', { n: fmtNumber(s.active_partners) })}
        />
        <StatTile
          loading={loading}
          label={t('partnerAnalytics.kpiSuccessRate')}
          value={fmtPercent(s.success, total)}
          tone={rateTone(s.success, total)}
          hint={t('partnerAnalytics.kpiSuccessHint', { n: fmtNumber(s.success) })}
        />
        <StatTile
          loading={loading}
          label={t('partnerAnalytics.kpiFailed')}
          value={fmtNumber(failed)}
          hint={t('partnerAnalytics.kpiFailedHint', {
            auth: fmtNumber(s.auth_failed),
            server: fmtNumber(s.server_errors),
          })}
        />
        <StatTile
          loading={loading}
          label={t('partnerAnalytics.kpiP95')}
          value={fmtMs(s.p95_ms)}
          hint={t('partnerAnalytics.kpiP95Hint', { ms: fmtMs(s.p50_ms) })}
        />
        <StatTile
          loading={loading}
          label={t('partnerAnalytics.kpiSearches')}
          value={fmtNumber(s.searches)}
          hint={t('partnerAnalytics.kpiSearchesHint', { n: fmtNumber(s.tours_returned) })}
        />
        <StatTile
          loading={loading}
          label={t('partnerAnalytics.kpiEmpty')}
          value={fmtPercent(s.searches_empty, tracked)}
          hint={tracked ? t('partnerAnalytics.kpiEmptyHint', { n: fmtNumber(tracked) }) : t('partnerAnalytics.kpiEmptyNone')}
        />
      </Box>

      <Box sx={{ display: 'grid', gap: 1.5, mb: 1.5, gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' } }}>
        <Panel
          title={t('partnerAnalytics.trafficTitle')}
          subtitle={t(data?.unit === 'hour' ? 'partnerAnalytics.trafficHourly' : 'partnerAnalytics.trafficDaily')}
          action={<Legend series={series} />}
        >
          <StackedBarChart data={chartData} series={series} loading={loading} emptyText={t('partnerAnalytics.noData')} />
        </Panel>
        <Panel title={t('partnerAnalytics.mixTitle')} subtitle={t('partnerAnalytics.mixSubtitle')}>
          {loading ? (
            <Skeleton variant="rounded" height={90} />
          ) : (
            <>
              <SegmentBar parts={mix} />
              {Number(s.unidentified) > 0 ? (
                <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 2.5 }}>
                  {t('partnerAnalytics.unidentified', { n: fmtNumber(s.unidentified) })}
                </Typography>
              ) : null}
            </>
          )}
        </Panel>
      </Box>

      <Panel title={t('partnerAnalytics.partnersTitle')} subtitle={t('partnerAnalytics.partnersSubtitle')} sx={{ mb: 1.5 }} contentSx={{ px: 0, pb: 1 }}>
        <TableContainer>
          <Table size="small" sx={{ minWidth: 860 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ pl: 2.5 }}>{t('partnerAnalytics.colPartner')}</TableCell>
                <TableCell sx={num}>{t('partnerAnalytics.colRequests')}</TableCell>
                <TableCell sx={num}>{t('partnerAnalytics.colSuccessRate')}</TableCell>
                <TableCell sx={num}>{t('partnerAnalytics.colFailed')}</TableCell>
                <TableCell sx={num}>{t('partnerAnalytics.colSearches')}</TableCell>
                <TableCell sx={num}>{t('partnerAnalytics.colEmpty')}</TableCell>
                <TableCell sx={num}>{t('partnerAnalytics.colLatency')}</TableCell>
                <TableCell sx={{ ...num, pr: 2.5 }}>{t('partnerAnalytics.colLastSeen')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <SkeletonRows cols={8} />
              ) : !data?.partners?.length ? (
                <EmptyRow colSpan={8} text={t('partnerAnalytics.noPartners')} />
              ) : (
                data.partners.map((p) => {
                  const tone = rateTone(p.success, p.total);
                  return (
                    <TableRow
                      key={p.id}
                      hover
                      onClick={() => setPartnerId(partnerId === String(p.id) ? '' : String(p.id))}
                      sx={{ cursor: 'pointer', '&:last-child td': { borderBottom: 'none' } }}
                    >
                      <TableCell sx={{ pl: 2.5 }}>
                        <Typography sx={{ fontSize: 13.5, fontWeight: 500 }}>{p.name || `#${p.id}`}</Typography>
                        <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>{p.ip_address || '—'}</Typography>
                      </TableCell>
                      <TableCell sx={num}>{fmtNumber(p.total)}</TableCell>
                      <TableCell sx={num}>
                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                          {tone ? <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: colors[tone] }} /> : null}
                          {fmtPercent(p.success, p.total)}
                        </Box>
                      </TableCell>
                      <TableCell sx={num}>{fmtNumber(p.failed)}</TableCell>
                      <TableCell sx={num}>{fmtNumber(p.searches)}</TableCell>
                      <TableCell sx={num}>{fmtPercent(p.searches_empty, p.searches_tracked)}</TableCell>
                      <TableCell sx={num}>
                        {fmtMs(p.avg_ms)}
                        <Box component="span" sx={{ color: 'text.disabled' }}> / {fmtMs(p.p95_ms)}</Box>
                      </TableCell>
                      <TableCell sx={{ ...num, pr: 2.5, color: p.last_seen ? 'text.primary' : 'text.disabled' }}>
                        {p.last_seen ? formatRelative(p.last_seen, t) : t('partnerAnalytics.never')}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Panel>

      <Box sx={{ display: 'grid', gap: 1.5, mb: 1.5, gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' } }}>
        <Panel title={t('partnerAnalytics.countriesTitle')} subtitle={t('partnerAnalytics.countriesSubtitle')}>
          <BarList items={countries} color={colors.series[0]} loading={loading} emptyText={t('partnerAnalytics.noData')} />
        </Panel>
        <Panel title={t('partnerAnalytics.endpointsTitle')} contentSx={{ px: 0, pb: 1 }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ pl: 2.5 }}>{t('partnerAnalytics.colEndpoint')}</TableCell>
                  <TableCell sx={num}>{t('partnerAnalytics.colRequests')}</TableCell>
                  <TableCell sx={num}>{t('partnerAnalytics.colSuccessRate')}</TableCell>
                  <TableCell sx={{ ...num, pr: 2.5 }}>{t('partnerAnalytics.colLatency')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <SkeletonRows cols={4} rows={3} />
                ) : !data?.endpoints?.length ? (
                  <EmptyRow colSpan={4} text={t('partnerAnalytics.noData')} />
                ) : (
                  data.endpoints.map((e) => (
                    <TableRow key={e.endpoint} sx={{ '&:last-child td': { borderBottom: 'none' } }}>
                      <TableCell sx={{ pl: 2.5, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12.5 }}>
                        {e.endpoint || '/'}
                      </TableCell>
                      <TableCell sx={num}>{fmtNumber(e.total)}</TableCell>
                      <TableCell sx={num}>{fmtPercent(e.success, e.total)}</TableCell>
                      <TableCell sx={{ ...num, pr: 2.5 }}>
                        {fmtMs(e.avg_ms)}
                        <Box component="span" sx={{ color: 'text.disabled' }}> / {fmtMs(e.p95_ms)}</Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Panel>
      </Box>

      <Panel title={t('partnerAnalytics.errorsTitle')} subtitle={t('partnerAnalytics.errorsSubtitle')} contentSx={{ px: 0, pb: 1 }}>
        <TableContainer>
          <Table size="small" sx={{ minWidth: 760 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ pl: 2.5 }}>{t('partnerAnalytics.colTime')}</TableCell>
                <TableCell>{t('partnerAnalytics.colPartner')}</TableCell>
                <TableCell>{t('partnerAnalytics.colEndpoint')}</TableCell>
                <TableCell>{t('partnerAnalytics.colStatus')}</TableCell>
                <TableCell>{t('partnerAnalytics.colMessage')}</TableCell>
                <TableCell sx={{ ...num, pr: 2.5 }}>{t('partnerAnalytics.colDuration')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <SkeletonRows cols={6} rows={3} />
              ) : !data?.recent_errors?.length ? (
                <EmptyRow colSpan={6} text={t('partnerAnalytics.noErrors')} />
              ) : (
                data.recent_errors.map((e) => (
                  <TableRow key={e.id} sx={{ '&:last-child td': { borderBottom: 'none' } }}>
                    <TableCell sx={{ pl: 2.5, whiteSpace: 'nowrap', color: 'text.secondary' }}>{formatDateTime(e.created_at)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {e.partner_name || (e.partner_id ? `#${e.partner_id}` : <Box component="span" sx={{ color: 'text.disabled' }}>{t('partnerAnalytics.unknownPartner')}</Box>)}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12.5 }}>
                      {String(e.endpoint || '').replace(/^.*\/partner/, '')}
                    </TableCell>
                    <TableCell>
                      <StatusChip code={e.status_code} />
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary', maxWidth: 360 }}>
                      <Typography noWrap title={e.error_message || ''} sx={{ fontSize: 13 }}>
                        {e.error_message || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ ...num, pr: 2.5 }}>{fmtMs(e.response_time_ms)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Panel>
    </Box>
  );
}
